import { useQuery } from '@tanstack/react-query'
import { getMyClients } from 'enrollment-platform/api/persons'
import { useCallback, useEffect, useState } from 'react'
import { CompanyListType } from '../types'
import { getHrisSyncCensusData } from './hris-sync-census-api'

function useCencusApiActivity() {
  const [selectedClientId, setSelectedClientId] = useState<string>('')
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  })
  const [companyList, setCompanyList] = useState<CompanyListType[]>([])

  const { isPending, error, data, isError, refetch } = useQuery({
    queryKey: ['census', pagination.page, pagination.limit, selectedClientId],
    queryFn: () =>
      getHrisSyncCensusData({
        page: pagination.page,
        limit: pagination.limit,
        clientPublicKey: selectedClientId || undefined,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  const handleCompanyChange = useCallback(
    (value: string) => {
      const selectedCompany = companyList.find((c) => c.id === value)
      setSelectedClientId(selectedCompany?.id || '')
      setPagination((prev) => ({ ...prev, page: 1 })) // Reset to page 1
    },
    [companyList]
  )

  const handlePaginationChange = useCallback(
    (page: number, pageSize?: number) => {
      setPagination((prev) => ({
        ...prev,
        page,
        limit: pageSize || prev.limit,
      }))
    },
    []
  )

  useEffect(() => {
    const getClients = async () => {
      const customers: any[] = await getMyClients()
      const customersPayrollEnabled = customers
        .filter((c) => c?.hrisConfiguration?.isCensusSyncEnabled)
        .map((c) => ({ id: c.id, companyName: c.name }))
      setCompanyList(customersPayrollEnabled)
    }
    getClients()
  }, [])

  return {
    data,
    refetch,
    isPending,
    isError,
    error,
    handleCompanyChange,
    handlePaginationChange,
    pagination,
    selectedClientId,
    companyList,
  }
}

export default useCencusApiActivity
